import React, { useEffect, useState } from 'react';
import {
  confirmedOrderHoldReason,
  confirmHeldEmailErrorMessage,
  confirmHeldEmailOrder,
  heldEmailNotAnOrderErrorMessage,
  holdCandidateLabel,
  markHeldEmailNotAnOrder,
  type ConfirmedOrderHoldDto,
} from '../../lib/confirmedOrderApi';

interface Props {
  emailId: string;
  hold?: ConfirmedOrderHoldDto | null;
}

type Done = { kind: 'order'; orderId: string; message?: string } | { kind: 'rfq'; rfqCount: number };

/**
 * D45.7 (owner, 2026-09-17): an email that confirms a quotation but was not confirmed
 * automatically — the quotation was not sent (D45.5), or the sender is not the client and the
 * email is not in the RFQ's conversation (D45.6) — waits here with the reason, and a person
 * confirms it. Confirm runs the manual confirmation, so the Settings switch and the confirmation
 * gate still apply; a refusal is shown from its `code`.
 *
 * D81 (owner-approved flow, 2026-09-28): the email may look like an order without clearly
 * confirming one (WORDING_UNCLEAR), or could confirm more than one quotation (QUOTATION_AMBIGUOUS
 * — a person picks from the hold's `candidates`, and Confirm sends the pick). On any hold a person
 * may instead say it is not an order: its RFQ is made and the hold cleared.
 */
export const ConfirmedOrderHoldNotice: React.FC<Props> = ({ emailId, hold }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [picked, setPicked] = useState('');

  // What was said and picked belongs to the email it was said about.
  useEffect(() => {
    setError(null);
    setDone(null);
    setPicked('');
  }, [emailId]);

  if (done?.kind === 'order') {
    return (
      <div role="status" className="mb-4 rounded-md border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
        Order confirmed.{done.message ? ` ${done.message}` : ''}{' '}
        <a href={`/confirmed-orders/${done.orderId}`} className="font-semibold underline">
          Open the order
        </a>
      </div>
    );
  }
  if (done?.kind === 'rfq') {
    return (
      <div role="status" className="mb-4 rounded-md border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
        Not an order.{' '}
        {done.rfqCount > 0
          ? `${done.rfqCount} RFQ${done.rfqCount === 1 ? ' was' : 's were'} made from it — it is in the RFQ tab.`
          : 'Nothing in it could be made into an RFQ.'}
      </div>
    );
  }

  if (!hold) return null;
  const ambiguous = hold.code === 'QUOTATION_AMBIGUOUS';

  const run = async (action: () => Promise<Done>, errorMessage: (e: unknown) => string) => {
    setSaving(true);
    setError(null);
    try {
      setDone(await action());
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = () =>
    run(async () => {
      // D81 decision 2: the pick is sent only on a hold that asks for one.
      const { confirmedOrder, message } = ambiguous
        ? await confirmHeldEmailOrder(emailId, picked)
        : await confirmHeldEmailOrder(emailId);
      return { kind: 'order', orderId: confirmedOrder._id, message };
    }, confirmHeldEmailErrorMessage);

  const handleNotAnOrder = () =>
    run(async () => ({ kind: 'rfq', rfqCount: (await markHeldEmailNotAnOrder(emailId)).rfqCount }), heldEmailNotAnOrderErrorMessage);

  return (
    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <div>
        <span className="font-semibold">Not confirmed automatically:</span> {confirmedOrderHoldReason(hold)}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {ambiguous && (
          <select
            aria-label="Quotation the client confirmed"
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            className="h-7 rounded-md border border-amber-300 bg-white px-2 text-xs"
          >
            <option value="">Pick the quotation…</option>
            {(hold.candidates || []).map((c) => (
              <option key={c.quotationId} value={c.quotationId}>
                {holdCandidateLabel(c)}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={saving || (ambiguous && !picked)}
          className="px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-60"
        >
          {saving ? 'Working…' : 'Confirm order'}
        </button>
        <button
          type="button"
          onClick={handleNotAnOrder}
          disabled={saving}
          className="px-3 py-1.5 rounded-md border border-amber-300 bg-white text-amber-900 text-xs font-semibold hover:bg-amber-100 disabled:opacity-60"
        >
          Not an order — make it an RFQ
        </button>
      </div>
      {error && (
        <div role="alert" className="mt-2 whitespace-pre-line text-rose-700">
          {error}
        </div>
      )}
    </div>
  );
};
